import { Howl, Howler } from 'howler';

// Wrapper around Howler that silently no-ops when an sfx file is missing,
// so the game runs fine even before audio assets are dropped in.

export class AudioSystem {
  constructor() {
    this.sfx = new Map();
    this._sfxBaseVolumes = new Map();
    this._musicBaseVolumes = new Map();
    this.music = null;
    this._currentMusicKey = null;
    this._pendingMusicTimer = null;
    this.masterVolume = 0.8;
    this.sfxVolume = 0.9;
    this.musicVolume = 0.45;
    this._muted = false;
    Howler.volume(this.masterVolume);
  }

  registerSfx(key, src, { volume = 1, rate = 1, pool = 4 } = {}) {
    const sources = Array.isArray(src) ? src : [src];
    try {
      const howl = new Howl({
        src: sources,
        volume: volume * this.sfxVolume,
        rate,
        pool,
        onloaderror: () => {
          // silently drop so missing sfx never crash the game
          this.sfx.set(key, null);
          this._sfxBaseVolumes.delete(key);
        },
      });
      this.sfx.set(key, howl);
      this._sfxBaseVolumes.set(key, volume);
    } catch (_e) {
      this.sfx.set(key, null);
      this._sfxBaseVolumes.delete(key);
    }
  }

  play(key, { volume, rate, pitchJitter = 0 } = {}) {
    const h = this.sfx.get(key);
    if (!h) return -1;
    try {
      const id = h.play();
      const base = this._sfxBaseVolumes.get(key) ?? 1;
      const requested = volume ?? base;
      h.volume(requested * this.sfxVolume, id);
      const r = (rate ?? 1) * (1 + (Math.random() * 2 - 1) * pitchJitter);
      h.rate(Math.max(0.2, r), id);
      return id;
    } catch (_e) { return -1; }
  }

  registerMusic(key, src, { volume = 1 } = {}) {
    const sources = Array.isArray(src) ? src : [src];
    try {
      const howl = new Howl({
        src: sources,
        volume: volume * this.musicVolume,
        loop: true,
        preload: true,
        // Web Audio avoids HTML5 single-element quirks when swapping long looping tracks.
        html5: false,
        onloaderror: () => {
          this.sfx.set(`music:${key}`, null);
          this._musicBaseVolumes.delete(key);
        },
      });
      this.sfx.set(`music:${key}`, howl);
      this._musicBaseVolumes.set(key, volume);
    } catch (_e) {
      this.sfx.set(`music:${key}`, null);
      this._musicBaseVolumes.delete(key);
    }
  }

  playMusic(key, { fadeInMs = 800, fadeOutPreviousMs = 400 } = {}) {
    // Allow switching (e.g. back to menu) while a fade-to-next-track is scheduled; key may still match the outgoing track.
    if (this._currentMusicKey === key && this.music && this._pendingMusicTimer == null) return;

    this._clearPendingMusicSchedule();

    const h = this.sfx.get(`music:${key}`);
    const base = this._musicBaseVolumes.get(key) ?? 1;
    const targetVolume = base * this.musicVolume;

    const prev = this.music;

    const applyNewTrack = () => {
      if (!h) {
        this.music = null;
        this._currentMusicKey = key;
        return;
      }
      try {
        if (fadeInMs <= 0) {
          h.volume(targetVolume);
          h.play();
        } else {
          h.volume(0);
          h.play();
          h.fade(0, targetVolume, fadeInMs);
        }
        this.music = h;
        this._currentMusicKey = key;
      } catch (_e) {
        this.music = null;
        this._currentMusicKey = key;
      }
    };

    if (prev) {
      if (fadeOutPreviousMs <= 0) {
        try {
          prev.stop();
        } catch (_e) {}
        applyNewTrack();
      } else {
        try {
          prev.fade(prev.volume(), 0, fadeOutPreviousMs);
        } catch (_e) {}
        const delay = fadeOutPreviousMs + 40;
        this._pendingMusicTimer = setTimeout(() => {
          this._pendingMusicTimer = null;
          try {
            prev.stop();
          } catch (_e) {}
          applyNewTrack();
        }, delay);
      }
    } else {
      applyNewTrack();
    }
  }

  _clearPendingMusicSchedule() {
    if (this._pendingMusicTimer != null) {
      clearTimeout(this._pendingMusicTimer);
      this._pendingMusicTimer = null;
    }
  }

  stopMusic({ fadeMs = 400 } = {}) {
    this._clearPendingMusicSchedule();
    const prev = this.music;
    if (prev) {
      try {
        prev.fade(prev.volume(), 0, fadeMs);
        setTimeout(() => {
          try {
            prev.stop();
          } catch (_e) {}
        }, fadeMs + 40);
      } catch (_e) {}
    }
    this.music = null;
    this._currentMusicKey = null;
  }

  setMuted(m) {
    this._muted = m;
    Howler.mute(m);
  }

  setMasterVolume(volume) {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    Howler.volume(this.masterVolume);
  }

  setSfxVolume(volume) {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    for (const [key, howl] of this.sfx.entries()) {
      if (!howl || key.startsWith('music:')) continue;
      const base = this._sfxBaseVolumes.get(key) ?? 1;
      howl.volume(base * this.sfxVolume);
    }
  }

  setMusicVolume(volume) {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    for (const [key, base] of this._musicBaseVolumes.entries()) {
      const howl = this.sfx.get(`music:${key}`);
      if (!howl) continue;
      howl.volume(base * this.musicVolume);
    }
  }
}
